import type { ReactNode } from "react";

/* ---------------------------------------------------------------------------
 * Superadmin shared UI kit — modern light SaaS look.
 * Cards with soft shadows, refined headers, skeletons, SVG charts.
 * ------------------------------------------------------------------------- */

/** Consistent page header: title + subtitle on the left, actions on the right. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 tracking-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-foreground-500 mt-1">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Standard card container. */
export function Card({
  children,
  className = "",
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={`bg-background-50 border border-background-200/80 rounded-xl shadow-[0_1px_2px_0_rgb(0_0_0/0.04)] ${
        padded ? "p-5" : ""
      } ${className}`}
    >
      {children}
    </div>
  );
}

/** Card with a titled header row (title left, action right). */
export function SectionCard({
  title,
  icon,
  action,
  children,
  className = "",
}: {
  title: string;
  icon?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={className} padded={false}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-background-100">
        <h3 className="flex items-center gap-2 text-sm font-heading font-semibold text-foreground-900">
          {icon && <i className={`${icon} text-primary-500`}></i>}
          {title}
        </h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

const TONES = {
  primary: "bg-primary-50 text-primary-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  violet: "bg-violet-50 text-violet-600",
  sky: "bg-sky-50 text-sky-600",
  rose: "bg-rose-50 text-rose-600",
  slate: "bg-slate-100 text-slate-600",
} as const;
export type Tone = keyof typeof TONES;

/** KPI stat card: icon tile, label, big value, optional delta / hint. */
export function StatCard({
  label,
  value,
  icon,
  tone = "primary",
  delta,
  deltaUp,
  hint,
}: {
  label: string;
  value: ReactNode;
  icon: string;
  tone?: Tone;
  delta?: string;
  deltaUp?: boolean;
  hint?: string;
}) {
  return (
    <Card className="p-4 md:p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <span
          className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${TONES[tone]}`}
        >
          <i className={`${icon} text-lg`}></i>
        </span>
        {delta && (
          <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-semibold ${
              deltaUp
                ? "bg-emerald-50 text-emerald-600"
                : "bg-rose-50 text-rose-600"
            }`}
          >
            <i className={deltaUp ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i>
            {delta}
          </span>
        )}
      </div>
      <p className="text-2xl font-heading font-bold text-foreground-900 mt-3 tracking-tight">
        {value}
      </p>
      <p className="text-xs text-foreground-500 font-medium mt-0.5">
        {label}
        {hint && <span className="text-foreground-400"> · {hint}</span>}
      </p>
    </Card>
  );
}

/** Status pill. */
export function Badge({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: Tone;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${TONES[tone]}`}
    >
      {children}
    </span>
  );
}

/** Inline error banner with retry. */
export function ErrorBanner({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-sm">
      <span className="flex items-center gap-2 min-w-0">
        <i className="ri-error-warning-line shrink-0"></i>
        <span className="truncate">{message}</span>
      </span>
      {onRetry && (
        <button
          onClick={onRetry}
          className="font-semibold hover:underline cursor-pointer whitespace-nowrap"
        >
          Retry
        </button>
      )}
    </div>
  );
}

/** Empty-state block for tables / lists. */
export function EmptyState({
  icon = "ri-inbox-2-line",
  title,
  hint,
  action,
}: {
  icon?: string;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-100 text-foreground-400 mb-3">
        <i className={`${icon} text-xl`}></i>
      </span>
      <p className="text-sm font-medium text-foreground-700">{title}</p>
      {hint && <p className="text-xs text-foreground-400 mt-1 max-w-xs">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Shimmering skeleton loader block. */
export function Skeleton({ className = "h-4 w-full" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-md bg-background-200/70 ${className}`} />
  );
}

/** Full-page loading grid of skeleton cards. */
export function PageSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} className="p-4 space-y-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-7 w-24" />
            <Skeleton className="h-3 w-32" />
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <Card key={i} className="space-y-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-40 w-full rounded-lg" />
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------- Charts ---------------------------------- */

/** Smooth SVG area chart. */
export function AreaChart({
  data,
  labels,
  format = (v) => v.toLocaleString("en-IN"),
  height = 160,
  color = "var(--tw-color, oklch(var(--primary-500)))",
}: {
  data: number[];
  labels: string[];
  format?: (v: number) => string;
  height?: number;
  color?: string;
}) {
  if (!data.length) return null;
  const w = 100;
  const h = 40;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const pts = data.map((v, i) => ({
    x: data.length > 1 ? i * step : w / 2,
    y: h - ((v - min) / range) * (h - 6) - 3,
  }));
  // Catmull-Rom → bezier for a smooth line
  const path = pts
    .map((p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const p0 = pts[i - 1];
      const cx = (p0.x + p.x) / 2;
      return `C ${cx} ${p0.y}, ${cx} ${p.y}, ${p.x} ${p.y}`;
    })
    .join(" ");
  const area = `${path} L ${pts[pts.length - 1].x} ${h} L ${pts[0].x} ${h} Z`;
  const gid = `ag-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{ height, width: "100%" }}
      >
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1="0"
            x2={w}
            y1={h * f}
            y2={h * f}
            stroke="oklch(var(--background-200))"
            strokeWidth="0.3"
          />
        ))}
        <path d={area} fill={`url(#${gid})`} />
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth="0.9"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
        />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="0.8" fill={color}>
            <title>{`${labels[i]}: ${format(data[i])}`}</title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between mt-1.5">
        {labels.map((l, i) => (
          <span
            key={`${l}-${i}`}
            className="text-[9px] text-foreground-400 whitespace-nowrap"
          >
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Rounded vertical bar chart. */
export function BarChart({
  data,
  labels,
  format = (v) => v.toLocaleString("en-IN"),
  height = 160,
  colorClass = "bg-primary-400 hover:bg-primary-500",
}: {
  data: number[];
  labels: string[];
  format?: (v: number) => string;
  height?: number;
  colorClass?: string;
}) {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  return (
    <div>
      <div className="flex items-end gap-1.5" style={{ height }}>
        {data.map((v, i) => (
          <div
            key={i}
            className="flex-1 flex flex-col justify-end h-full group relative"
            title={`${labels[i]}: ${format(v)}`}
          >
            <div
              className={`w-full rounded-md transition-colors ${colorClass}`}
              style={{ height: `${Math.max((v / max) * 100, 2)}%` }}
            ></div>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mt-1.5">
        {labels.map((l, i) => (
          <span
            key={`${l}-${i}`}
            className="flex-1 text-center text-[9px] text-foreground-400 whitespace-nowrap overflow-hidden"
          >
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}

const DONUT_COLORS = [
  "oklch(var(--primary-500))",
  "oklch(0.72 0.15 160)", // emerald
  "oklch(0.80 0.14 85)", // amber
  "oklch(0.65 0.18 295)", // violet
  "oklch(0.70 0.13 240)", // sky
  "oklch(0.65 0.20 20)", // rose
];

/** Donut chart with legend. */
export function DonutChart({
  segments,
  centerLabel,
  centerValue,
}: {
  segments: { label: string; value: number }[];
  centerLabel?: string;
  centerValue?: string;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const R = 15.9155; // circumference = 100
  let offset = 25;

  return (
    <div className="flex items-center gap-6 flex-wrap">
      <div className="relative w-36 h-36 shrink-0">
        <svg viewBox="0 0 42 42" className="w-full h-full -rotate-0">
          <circle
            cx="21"
            cy="21"
            r={R}
            fill="none"
            stroke="oklch(var(--background-100))"
            strokeWidth="5"
          />
          {segments.map((s, i) => {
            const pct = (s.value / total) * 100;
            const el = (
              <circle
                key={s.label}
                cx="21"
                cy="21"
                r={R}
                fill="none"
                stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={`${Math.max(pct - 1.5, 0.5)} ${100 - Math.max(pct - 1.5, 0.5)}`}
                strokeDashoffset={offset}
              >
                <title>{`${s.label}: ${s.value} (${pct.toFixed(1)}%)`}</title>
              </circle>
            );
            offset -= pct;
            return el;
          })}
        </svg>
        {(centerLabel || centerValue) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {centerValue && (
              <span className="text-xl font-heading font-bold text-foreground-900">
                {centerValue}
              </span>
            )}
            {centerLabel && (
              <span className="text-[10px] text-foreground-400 font-medium">
                {centerLabel}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[140px] space-y-2">
        {segments.map((s, i) => (
          <div key={s.label} className="flex items-center gap-2 text-xs">
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }}
            ></span>
            <span className="text-foreground-600 font-medium flex-1 truncate">
              {s.label}
            </span>
            <span className="text-foreground-900 font-semibold">{s.value}</span>
            <span className="text-foreground-400 w-10 text-right">
              {((s.value / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Horizontal labelled progress rows (status breakdowns etc.). */
export function ProgressRows({
  rows,
}: {
  rows: { label: string; value: number; colorClass: string }[];
}) {
  const total = rows.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <div className="space-y-3.5">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-foreground-600 font-medium">{r.label}</span>
            <span className="text-foreground-900 font-semibold">
              {r.value}
              <span className="text-foreground-400 font-normal ml-1.5">
                {((r.value / total) * 100).toFixed(0)}%
              </span>
            </span>
          </div>
          <div className="h-2 rounded-full bg-background-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${r.colorClass}`}
              style={{ width: `${(r.value / total) * 100}%` }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ Buttons ---------------------------------- */

export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = "button",
  className = "",
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 active:bg-primary-700 rounded-lg shadow-sm transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  onClick,
  disabled,
  title,
  className = "",
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 bg-background-50 hover:bg-background-100 border border-background-200 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 ${className}`}
    >
      {children}
    </button>
  );
}

/** Icon-only ghost button (refresh, close, etc.). */
export function IconButton({
  icon,
  onClick,
  title,
  spinning,
  className = "",
}: {
  icon: string;
  onClick?: () => void;
  title?: string;
  spinning?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-800 bg-background-50 hover:bg-background-100 border border-background-200 transition-colors cursor-pointer ${className}`}
    >
      <i className={`${icon} ${spinning ? "animate-spin" : ""}`}></i>
    </button>
  );
}
